import { Component, ChangeDetectionStrategy, Input, InjectionToken, Optional, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, switchMap, finalize } from 'rxjs/operators';
import { Accession } from '../accession';
import { AccessionsStorage, AccessionsSearcher } from '../accessions-storage';

export const CUSTOM_ACCESSIONS_STORAGE = new InjectionToken('Custom accessions storage');

@Component({
    selector: 'accession-search',
    template: require('./accession-search.component.html'),
    styles: [require('./accession-search.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessionSearchComponent {
    @Input()
    public label = 'Accession';

    @Input()
    public control: FormControl;

    @Input()
    public thresholdSearch = 5;

    public readonly isSearching$ = new BehaviorSubject(false);
    public accessions$: Observable<Accession[]>;
    public readonly searchFlow$ = new Subject<string>();
    public readonly requiredErrorName = 'required';
    private readonly accessionsStorage: AccessionsSearcher;

    constructor(
        accessionsStorage: AccessionsStorage,
        @Optional() @Inject(CUSTOM_ACCESSIONS_STORAGE) customAccessionsStorage: AccessionsSearcher,
    ) {
        this.accessionsStorage = customAccessionsStorage
            ? customAccessionsStorage
            : accessionsStorage;

        this.accessions$ = this.searchFlow$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => this.isSearching$.next(true)),
            switchMap(term => this.loadAccessions(term).pipe(
                finalize(() => this.isSearching$.next(false))
            )),
        );
    }

    public onSelectedAccessionChange(accessionSelects: Accession[]) {
        const accessionSelect = accessionSelects[0];
        if (accessionSelect) {
            this.control.setValue(accessionSelect);
        }
    }

    public getAccessionNames(accession: Accession): string {
        return `${accession.accessionNumber}(${accession.projectNumber})`;
    }

    private loadAccessions(searchTerm: string): Observable<Accession[]> {
        return searchTerm && searchTerm.length < this.thresholdSearch
            ? of([])
            : this.accessionsStorage.search(null, searchTerm);
    }
}
